<script setup>
import { useCountsStore } from "@/stores/counts";
import { useUserStore } from "@/stores/user";
import { useOrganizationStore } from "@/stores/organization.js";
import {
  formatCount,
  interactionStatusToThreadDFAStates,
} from "@/utils/utils.js";
import { useTeamStore } from "@/stores/team.js";
import { useDepartmentStore } from "@/stores/department.js";
import { useNavigation } from "@/composables/useNavigation.js";

import {
  InteractionPageEnum,
  ticketCategories,
  allSubMenus,
} from "./InboxLeftMenu.utils.js";
import CreateTicketModal from "@/components/ticket/CreateTicketModal.vue";
import {
  ThreadCategoryStatusEnum,
  TicketCategoryStatusEnum,
  ThreadStatusEnum,
} from "@/utils/constants.js";
import { useInboxDefaultParams } from "@/composables/useInboxDefaultParams.js";

const countsStore = useCountsStore();
const orgStore = useOrganizationStore();
const userStore = useUserStore();
const teamStore = useTeamStore();
const departmentStore = useDepartmentStore();
const { navigateTo, putQueryParams, queryParams } = useNavigation();
const { getDefaultInboxQueryParams } = useInboxDefaultParams();

const currentCategory = inject("CURRENT_CATEGORY");
const showCreateTicketModal = ref(false);

// Calculate counts based on statuses
const getCategoryCountByStatuses = (child) => {

  const interactionCategories = [
    InteractionPageEnum.ALL_INTERACTIONS,
    InteractionPageEnum.VA_INTERACTIONS,
    InteractionPageEnum.ALL_SOFT_ALERTS,
    InteractionPageEnum.MY_SOFT_ALERTS,
  ];
  
  if (interactionCategories.includes(child?.value)) {
    const count = countsStore.interactionCounts[child.countKey]?.total_count || 0;
    return formatCount(count);
  }

  const statusesIncludedInCount = [ 
    ThreadStatusEnum.NEW,
    ThreadStatusEnum.REOPENED,
    ThreadStatusEnum.ESCALATED,
    ThreadStatusEnum.OPEN,
    ThreadStatusEnum.PENDING_USER_REPLY,
  ];

  const countsByStatus = (
    countsStore.interactionCounts[child.countKey]?.thread_status_count_map ||
    {}
  );
  const count = statusesIncludedInCount.reduce(
    (acc, cur) => {
      const curCount = countsByStatus[cur] || 0
      return acc + curCount
    },
    0
  );
  return formatCount(count);
};

const getCountTooltip = (child) => {
  const category = child?.value || null;
  if (category === InteractionPageEnum.ALL_INTERACTIONS) return "Total Interactions";
  if (category === InteractionPageEnum.VA_INTERACTIONS) return "AI Interactions";
  if (
    category === InteractionPageEnum.ALL_TICKETS ||
    category === InteractionPageEnum.MY_TICKETS
  ) {
    return "Unresolved Tickets"
  }
  
  return "";
};

const toggleShowCreateTicketModal = () => {
  showCreateTicketModal.value = !showCreateTicketModal.value;
};

/** Navigate to an inbox category with fresh default params (used when user clicks sidebar). */
const goToInboxCategory = (child) => {
  navigateTo(child.route, getDefaultInboxQueryParams(child.value));
};

const allowedSubMenuChildren = (children) => {
  return children.filter((child) => {
    if (child.value === InteractionPageEnum.UNKNOWN_USER_TICKETS) {
      return orgStore.myOrgEnablements.includes("unknown_user_tickets");
    }
    return true;
  });
};

// When landing on an inbox page with no query params (e.g. direct nav or refresh), fill in defaults.
// When the URL already has params (e.g. pasted link), we leave them as-is.
onMounted(() => {
  const query = queryParams.value || {};
  const hasExistingParams = Object.keys(query).length > 0;
  if (!hasExistingParams && currentCategory?.value) {
    putQueryParams(getDefaultInboxQueryParams(currentCategory.value));
  }
});
</script>

<template>
  <div class="inbox-left-menu">
    <span class="text-white fw-semibold font-size-lg">Inbox</span>
    <div v-for="(subMenu, idx) in allSubMenus" :key="idx" class="sub-menu">
      <span v-if="subMenu.title" class="sub-menu-heading">
        {{ subMenu.title }}
      </span>
      <div class="d-flex flex-column" :class="{ 'mt-3': subMenu.title }">
        <div
          class="btn-group mb-3"
          v-if="subMenu.title === 'Tickets' && userStore.hasPerm('create-ticket')"
        >
          <custom-button
            has-font-semi-bold
            button-type="blue"
            @click="toggleShowCreateTicketModal"
          >
            Create Ticket
          </custom-button>
        </div>

        <a
          v-for="child in allowedSubMenuChildren(subMenu.children)"
          :key="child.value"
          class="sub-menu-item cursor-pointer"
          :class="{
            'sub-menu-item--active':
              currentCategory === child.value,
          }"
          @click.stop="goToInboxCategory(child)"
        >
          <span class="d-flex justify-content-between flex-grow-1">
            <span>{{ child.text }}</span>
            <span
              v-if="child.count && getCategoryCountByStatuses(child)"
              v-b-tooltip.hover.right="getCountTooltip(child)"
              class="d-flex align-items-center text-neutral-0"
            >
              <IconCircleFill v-if="child.displayCircle" class="circle-icon" />
              <span class="ms-1 text-neutral-0 fw-normal">
                {{ getCategoryCountByStatuses(child) }}
              </span>
            </span>
          </span>
        </a>
      </div>
    </div>
    <div class="spacer" />
  </div>

  <CreateTicketModal
    v-model="showCreateTicketModal"
    @close="toggleShowCreateTicketModal"
  />
</template>

<style scoped lang="scss">
.inbox-left-menu {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.spacer {
  flex-grow: 1;
}

.sub-menu {
  &:first-of-type {
    margin-top: 2rem;
  }

  &:not(:first-of-type) {
    margin-top: 4rem;
  }

  .sub-menu-heading {
    font-weight: bold;
    font-size: $font-size-sm;
    color: $neutral-250;
  }

  .sub-menu-item {
    text-decoration: none;
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    font-size: $font-size-sm;
    color: $neutral-300;
    border: 1px solid $neutral-500;

    &:hover {
      background: $neutral-450;
    }

    &:active {
      background: $neutral-450;
      border-color: $neutral-400;
    }

    &--active {
      font-weight: 600;
      background-color: $neutral-400;
      color: $neutral-0;
    }
  }

  .circle-icon {
    width: 8px;
    height: 8px;
    fill: var(--primary-250);
  }
}
</style>
